import type { ReactNode } from "react";
import { useState } from "react";
import { TextField, MenuItem, Autocomplete, createFilterOptions, Skeleton } from "@mui/material";
import type { Poste } from "models";
import { PosteBox } from "components/Business";
import { stringUtil } from "@sdeapps/react-core";

interface PosteAutoCompleteProps {
  value?: Poste | null;
  onChange?: (poste: Poste | null) => void;
  label?: string;
  placeholder?: string;
  postes: Array<Poste>;
  size?: "small" | "medium";
  isLoading?: boolean;
}

const filterOptions = createFilterOptions({
  trim: true,
  stringify: ({ agent: { matricule, nom, prenom }, libelle, codeAstre }: Poste) => {
    const normalizedNom = stringUtil.normalize(nom);
    const normalizedPrenom = stringUtil.normalize(prenom);
    const normalizedLibelle = stringUtil.normalize(libelle);

    return `${matricule} ${nom} ${prenom} ${nom} ${normalizedNom} ${normalizedPrenom} ${normalizedNom} ${codeAstre} ${libelle} ${normalizedLibelle}`;
  },
});

export function PosteAutoComplete({
  value,
  onChange,
  label,
  placeholder,
  postes,
  size = "medium",
  isLoading = false,
}: Readonly<PosteAutoCompleteProps>): ReactNode {
  const [input, setInput] = useState<string>("");

  if (isLoading) {
    return <Skeleton height={56} />;
  }

  return (
    <Autocomplete
      value={postes.find((o) => o.codeAstre === value?.codeAstre) ?? null}
      onChange={(_e, _value: Poste | null) => {
        onChange?.(_value);
      }}
      onInputChange={(_e, inputValue) => {
        setInput(inputValue);
      }}
      options={postes}
      filterOptions={filterOptions}
      fullWidth
      autoHighlight
      openOnFocus
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      size={size}
      isOptionEqualToValue={(o, v) => o.codeAstre === v.codeAstre}
      getOptionLabel={({ libelle }) => libelle}
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} />}
      renderValue={(poste) => input === "" && <PosteBox poste={poste} noWrap />}
      getOptionKey={({ codeAstre }) => codeAstre}
      renderOption={(props, poste) => (
        <MenuItem {...props} key={props.key}>
          <PosteBox poste={poste} noWrap />
        </MenuItem>
      )}
      // Vide l'input si l'utilisateur appuie sur Retour Arrière
      onKeyDown={(e) => {
        if (value != null && e.key === "Backspace") {
          onChange?.(null);
        }
      }}
      noOptionsText="Aucun Poste ne correspond à votre recherche."
    />
  );
}
